import React, { useEffect, useRef, useState, useCallback } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import LogoLoader from "./LogoLoader";
import "./AgoraStreamingClient.css";

// Agora client options
const clientOptions = {
  mode: "live",
  codec: "h264",
};

const AgoraStreamingClient = ({
  appId,
  channel,
  token = null,
  uid = null,
  role = "audience",
  videoSettings = {},
  onKicked = null,
  onJoinedUid = null,
}) => {
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isJoined, setIsJoined] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const agoraClientRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const videoContainerRef = useRef(null);

  // iOS detection
  const [isIOS] = useState(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return (
      /iphone|ipad|ipod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  });

  // Handle iOS video playback - fixed to accept userId as parameter
  const handleIOSVideoPlayback = useCallback((videoTrack, userId) => {
    const playVideo = async () => {
      try {
        const container = remoteVideoRefs.current[userId];
        if (!container) {
          setTimeout(() => playVideo(), 100);
          return;
        }
        await videoTrack.play(container, {
          fit: "contain",
          mirror: false,
        });
      } catch (err) {
        console.warn("iOS video playback error:", err);
        setTimeout(() => playVideo(), 500);
      }
    };
    playVideo();
  }, []);

  // Handle fullscreen toggle with cross-browser support
  const toggleFullscreen = () => {
    if (!videoContainerRef.current) return;

    const elem = videoContainerRef.current;
    const fullscreenEnabled =
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled;

    if (!fullscreenEnabled) {
      setIsFullscreen(!isFullscreen);
      return;
    }

    try {
      if (
        !document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !document.mozFullScreenElement &&
        !document.msFullscreenElement
      ) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen();
        } else if (elem.mozRequestFullScreen) {
          elem.mozRequestFullScreen();
        } else if (elem.msRequestFullscreen) {
          elem.msRequestFullscreen();
        } else {
          setIsFullscreen(true);
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        } else {
          setIsFullscreen(false);
        }
      }
    } catch (err) {
      setIsFullscreen(!isFullscreen);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!isInFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, []);

  // Helper function to set errors, filtering out certain known errors
  const setFilteredError = (err) => {
    if (
      err &&
      err.message &&
      (err.message.includes("Client already in connecting/connected state") ||
        err.message.includes("INVALID_OPERATION") ||
        err.message.includes("client-417d4"))
    ) {
      return;
    }
    setError(err);
  };

  useEffect(() => {
    if (!agoraClientRef.current) {
      agoraClientRef.current = AgoraRTC.createClient(clientOptions);
    }

    const setupAgoraEvents = () => {
      const client = agoraClientRef.current;

      // Handle connection state changes (including when kicked/banned)
      client.on("connection-state-change", (curState, revState, reason) => {
        console.log(`Agora connection state: ${revState} -> ${curState}, reason: ${reason}`);
        
        // Check if user was kicked/banned
        if (curState === "DISCONNECTED" && reason === "BANNED") {
          console.log("User has been KICKED/BANNED from the channel by administrator");
          setIsJoined(false);
          setError("You have been removed from this stream by the administrator.");
          if (onKicked) {
            onKicked({ reason: "BANNED", message: "Kicked by administrator" });
          }
        }
      });

      client.on("user-published", async (user, mediaType) => {
        setIsLoading(false);
        try {
          await client.subscribe(user, mediaType);

          if (mediaType === "video") {
            setRemoteUsers((prevUsers) => {
              if (!prevUsers.includes(user.uid)) {
                if (!activeUser && prevUsers.length === 0) {
                  setActiveUser(user.uid);
                }
                return [...prevUsers, user.uid];
              }
              return prevUsers;
            });

            // iOS-specific video handling with fixed parameter passing
            if (isIOS) {
              setTimeout(() => {
                handleIOSVideoPlayback(user.videoTrack, user.uid);
              }, 100);
            } else {
              setTimeout(() => {
                if (remoteVideoRefs.current[user.uid]) {
                  user.videoTrack.play(remoteVideoRefs.current[user.uid], {
                    fit: "contain",
                    mirror: false,
                    setupOptions: {
                      width: "100%",
                      height: "100%",
                    },
                  });
                }
              }, 0);
            }
          }

          if (mediaType === "audio") {
            user.audioTrack.play();
          }
        } catch (err) {
          console.error("Error subscribing to user:", err);
        }
      });

      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "video") {
          setRemoteUsers((prevUsers) => {
            const newUsers = prevUsers.filter((uid) => uid !== user.uid);
            if (activeUser === user.uid && newUsers.length > 0) {
              setActiveUser(newUsers[0]);
            }
            return newUsers;
          });
        }
      });

      client.on("user-left", (user) => {
        setRemoteUsers((prevUsers) => {
          const newUsers = prevUsers.filter((uid) => uid !== user.uid);
          if (activeUser === user.uid && newUsers.length > 0) {
            setActiveUser(newUsers[0]);
          }
          return newUsers;
        });
      });
    };

    setupAgoraEvents();

    const joinChannel = async () => {
      if (!appId || !channel) {
        setFilteredError(
          !appId ? "App ID is required" : "Channel name is required"
        );
        return;
      }

      try {
        await agoraClientRef.current.setClientRole(role);
        // join() returns the actual UID assigned by Agora (important when joining with uid=0)
        const joinedUid = await agoraClientRef.current.join(appId, channel, token, uid);
        console.log("Joined Agora channel with UID:", joinedUid);
        
        // Report the actual joined UID to parent component
        if (onJoinedUid) {
          onJoinedUid(joinedUid);
        }

        if (role === "host") {
          const constraints = isIOS
            ? {
                video: {
                  facingMode: "user",
                  width: {
                    ideal:
                      videoSettings.videoConfig?.encoderConfig?.width || 640,
                  },
                  height: {
                    ideal:
                      videoSettings.videoConfig?.encoderConfig?.height || 360,
                  },
                },
                audio: true,
              }
            : {};

          const [audioTrack, videoTrack] =
            await AgoraRTC.createMicrophoneAndCameraTracks(
              videoSettings.audioConfig || {},
              {
                ...(videoSettings.videoConfig || {}),
                mediaTrackConstraints: constraints,
              }
            );

          localAudioTrackRef.current = audioTrack;
          localVideoTrackRef.current = videoTrack;

          await agoraClientRef.current.publish([audioTrack, videoTrack]);

          if (isIOS) {
            handleIOSVideoPlayback(videoTrack, "local-video");
          } else {
            videoTrack.play("local-video");
          }
        }

        setIsJoined(true);
      } catch (err) {
        setFilteredError(err);
        console.error("Error joining channel:", err);
        setIsLoading(false);
      }
    };

    joinChannel();

    return () => {
      const leaveChannel = async () => {
        if (localAudioTrackRef.current) {
          localAudioTrackRef.current.close();
        }
        if (localVideoTrackRef.current) {
          localVideoTrackRef.current.close();
        }
        try {
          await agoraClientRef.current.leave();
        } catch (err) {
          console.log("Error leaving channel:", err);
        }
        setIsJoined(false);
        setRemoteUsers([]);
      };

      if (isJoined) {
        leaveChannel();
      }
    };
  }, [
    appId,
    channel,
    token,
    uid,
    role,
    activeUser,
    videoSettings,
    isIOS,
    handleIOSVideoPlayback,
  ]);

  const setUserAsFocused = (uid) => {
    setActiveUser(uid);
  };

  return (
    <div
      className={`agora-streaming-container ${
        isFullscreen ? "fullscreen-mode" : ""
      } ${isIOS ? "ios-device" : ""}`}
      ref={videoContainerRef}
      onDoubleClick={toggleFullscreen}
    >
      {isLoading && remoteUsers.length === 0 && (
        <div className="loading-container">
          <LogoLoader size={80} message="Connecting to stream..." />
        </div>
      )}

      {role === "host" && (
        <div className="local-video-container">
          <div id="local-video" className="video-player"></div>
          <div className="video-label">You (Host)</div>
        </div>
      )}

      {remoteUsers.length > 0 ? (
        <div className="video-viewer">
          <div className="main-video-container">
            <div
              id={`main-video-${activeUser}`}
              className="main-video-player"
              ref={(el) => {
                if (activeUser) {
                  remoteVideoRefs.current[activeUser] = el;
                  if (el) {
                    const isPWALayout =
                      el.closest(".pwa-layout-content") !== null ||
                      el.closest(".pwa-layout") !== null;
                    const isMultiviewLayout =
                      el.closest(".multiview-layout") !== null ||
                      el.closest(".multiview-video") !== null;

                    if (isPWALayout || isMultiviewLayout) {
                      el.style.position = "";
                      el.style.top = "";
                      el.style.left = "";
                      el.style.transform = "";
                      el.style.width = "";
                      el.style.height = "";
                    } else {
                      el.style.position = "absolute";
                      el.style.top = "50%";
                      el.style.left = "50%";
                      el.style.transform = "translate(-50%, -50%)";
                    }
                  }
                }
              }}
            ></div>
          </div>

          {remoteUsers.length > 1 && (
            <div className="thumbnail-videos">
              {remoteUsers
                .filter((uid) => uid !== activeUser)
                .map((remoteUid) => (
                  <div
                    key={remoteUid}
                    className="thumbnail-container"
                    onClick={() => setUserAsFocused(remoteUid)}
                  >
                    <div
                      id={`remote-video-${remoteUid}`}
                      className="thumbnail-video"
                      ref={(el) => {
                        remoteVideoRefs.current[remoteUid] = el;
                      }}
                    ></div>
                  </div>
                ))}
            </div>
          )}
        </div>
      ) : (
        <div className="no-remote-users">
          {isJoined && !isLoading ? (
            <LogoLoader
              size={80}
              message="No stream available in this channel"
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AgoraStreamingClient;
